import { useContext } from "react";
import { FormProvider, useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";

import MyTextField from "../../components/inputs/MyTextField";
import MyButton from "../../components/MyButton";
import { useUser } from "../../hooks/useUser";
import { useAuth } from "../../hooks/useAuth";
import { SiteContext } from "../../context/SiteProvider";
import { AuthContext } from "../../context/AuthProvider";

const MyPage02 = () => {
  const { setLoadingScreen } = useContext(SiteContext);
  const { profile, setProfile, isAuthenticated } = useContext(AuthContext);
  const { changeEmailMutation } = useUser();
  const { checkUserIdMutation, checkEmailMutation } = useAuth();

  const initValues = {
    email: "",
    isSentOtp: false,
    otp: "",
    isConfirmedOtp: false,
  };

  const validationSchema = yup.object({
    email: yup
      .string()
      .email("이메일 형식이 올바르지 않습니다.")
      .required("이메일을 입력해주세요."),
    isSentOtp: yup.boolean().default(false),
    otp: yup.string().when("isSentOtp", {
      is: true,
      then: () => yup.string().required("필수 입력 항목입니다."),
    }),
    isConfirmedOtp: yup.boolean().default(false),
  });

  const methods = useForm({
    defaultValues: initValues,
    resolver: yupResolver(validationSchema),
  });

  const isSentOtp = methods.watch("isSentOtp");
  const isConfirmedOtp = methods.watch("isConfirmedOtp");

  const onVerifyEmail = async () => {
    const isValidEmail = await methods.trigger("email");

    if (isValidEmail) {
      methods.clearErrors("isSentOtp");
      const email = methods.getValues("email");

      setLoadingScreen(true);
      const payload = {
        userKeyType: "Email",
        userKeyCode: email,
      };

      checkUserIdMutation.mutate(payload, {
        onSuccess: (res) => {
          if (res.exists) {
            methods.setError("email", {
              type: "manual",
              message: "이미 등록된 이메일 주소입니다",
            });
            setLoadingScreen(false);
            return;
          } else {
            checkEmailMutation.mutate(payload, {
              onSuccess: (res) => {
                if (res?.userKeyToken) {
                  alert(
                    "입력하신 이메일주소로 인증번호가 발송되었습니다. 메일함을 확인해주세요."
                  );
                  methods.clearErrors("email");
                  methods.setValue("isSentOtp", true);
                  methods.setValue("userKeyToken", res.userKeyToken);
                  methods.trigger("otp");
                } else {
                  alert("이메일 전송 중 오류가 발생했습니다.");
                }
              },
              onError: () => {
                alert("이메일 전송 중 오류가 발생했습니다.");
              },
              onSettled: () => setLoadingScreen(false),
            });
          }
        },
      });
    }
  };

  const onSubmit = (data) => {
    methods.clearErrors("isConfirmed");

    if (!data.isSentOtp) {
      methods.setError("email", {
        message: "이메일을 먼저 확인해주세요.",
      });
      return;
    }

    if (!data.otp) {
      methods.setError("otp", {
        message: "필수 입력 항목입니다.",
      });
      return;
    }

    const payload = {
      userKeyType: "Email",
      userKeyToken: data.userKeyToken,
      pincode: data.otp,
    };

    setLoadingScreen(true);
    changeEmailMutation.mutate(
      { userId: profile.userId, payload },
      {
        onSuccess: (res) => {
          if (res) {
            methods.reset();
            setProfile({
              ...profile,
              email: res.email,
              lastModifiedDate: res.lastModifiedDate,
            });
            alert("이메일 변경이 완료되었습니다.");
          } else {
            methods.setError("otp", {
              message: "인증번호를 다시 확인해주세요.",
            });
          }
        },
        onError: () => {
          methods.setError("otp", {
            message: "인증번호를 다시 확인해주세요.",
          });
          return;
        },
        onSettled: () => {
          setLoadingScreen(false);
        },
      }
    );
  };

  return isAuthenticated ? (
    <div className="flex justify-center">
      <div className="w-full max-w-[600px] pt-10">
        <FormProvider {...methods}>
          <form
            onSubmit={methods.handleSubmit(onSubmit)}
            className="flex flex-wrap md:gap-x-[4%] gap-x-[2%] gap-y-3"
          >
            <div className="xs:w-[78%] w-full flex flex-col">
              <label htmlFor="email" className="w-[100px] font-semibold">
                이메일 <span className="text-error">*</span>
              </label>
              <div className="flex-1">
                <MyTextField
                  id="email"
                  name="email"
                  placeholder="신규 메일을 입력해주세요."
                />
              </div>
            </div>
            <MyButton
              variant="outlined"
              className="xs:w-[18%] w-full h-[56px] xs:mt-6"
              size="small"
              onClick={onVerifyEmail}
            >
              <span className="text-sm">인증하기</span>
            </MyButton>

            {isSentOtp && (
              <div className="w-full">
                <MyTextField
                  id="otp"
                  name="otp"
                  placeholder="인증번호를 입력해주세요."
                />
                {isConfirmedOtp && (
                  <p className="text-success text-sm">
                    인증번호가 확인되었습니다.
                  </p>
                )}
              </div>
            )}

            <MyButton
              variant="contained"
              color="primary"
              className="w-full font-semibold h-[56px]"
              type="submit"
              size="small"
            >
              이메일 변경하기
            </MyButton>
          </form>
        </FormProvider>
      </div>
    </div>
  ) : null;
};

export default MyPage02;
